import React, { useState } from 'react';
import { ShoppingCart } from 'lucide-react';
import { toast } from "sonner";
import { loadStripe } from '@stripe/stripe-js';
import { Button } from '@/components/ui/button';

const STRIPE_PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;

if (!STRIPE_PUBLISHABLE_KEY) {
  console.error('Stripe publishable key is missing. Please check your .env file and add VITE_STRIPE_PUBLISHABLE_KEY.');
}

const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY!);

interface CheckoutButtonProps {
  priceId: string;
  quantity?: number;
  disabled?: boolean;
  className?: string;
  children?: React.ReactNode;
}

const CheckoutButton = ({ 
  priceId,
  quantity = 1,
  disabled = false,
  className = "",
  children
}: CheckoutButtonProps) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleCheckout = async () => {
    if (isLoading || disabled) return;
    
    setIsLoading(true);
    try {
      if (!priceId) {
        throw new Error('No price ID provided');
      }

      if (!STRIPE_PUBLISHABLE_KEY) {
        throw new Error('Stripe publishable key is missing. Please check your configuration.');
      }

      const stripe = await stripePromise;
      if (!stripe) {
        throw new Error('Failed to initialize Stripe. Please check your configuration.');
      }

      toast.info('Creating your checkout session...', {
        duration: 2000,
      });

      // Create checkout session
      const response = await fetch('/.netlify/functions/api/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          priceId,
          quantity,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create checkout session');
      }

      const data = await response.json();
      
      // If we have a direct URL, redirect to it
      if (data.url) {
        window.location.href = data.url;
        return;
      }

      // Otherwise use redirectToCheckout with sessionId
      if (!data.sessionId) {
        throw new Error('No session ID or URL returned from server');
      }

      const { error } = await stripe.redirectToCheckout({
        sessionId: data.sessionId
      });

      if (error) {
        throw error;
      }

    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to initiate checkout. Please try again.', {
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Button
      onClick={handleCheckout}
      disabled={isLoading || disabled || !priceId || !STRIPE_PUBLISHABLE_KEY}
      className={className}
    >
      {isLoading ? (
        <div className="flex items-center space-x-2">
          <div className="h-5 w-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
          <span>Processing...</span>
        </div>
      ) : children || (
        <>
          <ShoppingCart className="mr-2 h-5 w-5" />
          <span>Buy Now</span>
        </>
      )}
    </Button>
  );
};

export default CheckoutButton;
